/*
  Advertorial offer page controller (sections/xall-adv-offer.liquid). No dependencies.

  Checkout handoff (no product form, so Appstle's helper has nothing to decorate):
  1. POST /cart/clear.js (section setting, default on)
  2. POST /cart/add.js: the selected package variant, plus the bump variant if checked; every
     line carries properties._offer_id. One-time lines carry no selling_plan key. The
     subscription package (data-mode="subscription", see sections/xall-adv-offer.liquid) sends
     selling_plan = its data-plan and adds its bonus line (data-bonus-variant x data-bonus-qty,
     one-time, made $0 by an automatic Buy X Get Y discount). The response is checked: the
     subscription line must come back on exactly that plan and every other line on none, else the
     handoff aborts and the added lines are removed.
     This one request uses the page's current fetch, so Shopify emits product_added_to_cart
     (Meta AddToCart); the other steps keep the fetch captured at load.
  3. POST /cart/update.js attributes: offer_id, angle, lander_path, offer_path, package, adv_cta,
     purchase_mode (subscription / one_time), offer_plan (the plan id, or blank)
     (the article CTA used), UTMs and click ids. Every key is sent (blank removes a stale value from an earlier visit). cart/clear
     keeps attributes, so the same payload also blanks the MPC lander's own keys
     (STALE_MPC_KEYS) and sends discount: '' to drop a code carried over from an earlier
     /discount/<code> visit (e.g. MPC-LANDER-FREESHIP). Other keys (email popup A/B, apps) are
     left alone. A failed or unconfirmed write is retried once; after that the handoff proceeds,
     logs a warning and emits adv_attr_fail, because losing the sale is worse than losing the stamp.
  4. Expire the discount_code cookie, then location -> /checkout. A /discount/<code> visit leaves
     that cookie, and checkout re-applies it even after discount: '' cleared the cart (verified
     live 2026-10-13: both steps are needed, neither alone removes the code).

  Selection memory: sessionStorage['xall_adv_offer_choice:<offer_id>'] holds the picked package
  variant and bump state; restored on load and on pageshow (back from checkout, bfcache, reload).

  Attribution source: sessionStorage['xall_adv_attribution'] written by assets/xall-adv.js on the
  article (and on this page), overlaid with this page's own URL parameters.

  Events (each one goes three ways, same payload):
  - window.dataLayer.push({ event: 'adv_offer_view', ... })  with GA4-style `ecommerce` and
    Meta-style `content_ids` / `value` / `currency` fields
  - document CustomEvent 'xall:adv_offer_view' (detail = payload)
  - Shopify.analytics.publish('xall_adv_offer_view', payload) for custom web pixels, when present
  Names: adv_offer_view, adv_package_select, adv_bump_toggle, adv_checkout_click,
  adv_checkout_error, adv_cart_error, adv_attr_fail. Every payload carries offer_id, angle, available, currency,
  plus adv_cta (the article CTA used) and landing_path (first advertorial page of the session)
  from the saved attribution, plus the package fields once one is selected. The article's own
  events (adv_scroll, adv_cta_seen, adv_cta_click) are documented in assets/xall-adv.js.

  Inside the article (root has data-xadv-offer-embedded, section placement "Inside the article"):
  - adv_offer_view fires once, the first time the package cards are at least half on screen (or
    fill half the viewport), not on load: on load it would count every article reader as an
    offer arrival.
  - lander_path is this page (the article and the buy box are the same page, so the saved
    landing_path equals it and the stand-alone rule would leave it blank).
  - adv_cta is the last article CTA clicked (sticky, inline-<n>, img-<block id>: they jump to the
    box), else "picker".

  Handoff failures (adv_cart_error, sent next to the older adv_checkout_error, which keeps its
  payload): step (clear, add or checkout), status (HTTP status, 0 when there was no response),
  reason, cta (the button tapped: main or sticky), path (location.pathname), error (short text).
  Reasons: http_error (non-2xx), bot_challenge (429, or an HTML page that reads like Shopify's
  "Verifying your connection" check), non_json (a 2xx that is not JSON, which used to pass as
  success), network_error, timeout (no answer in REQUEST_TIMEOUT_MS), selling_plan (a line came
  back on a plan), redirect_stalled (still on this page REDIRECT_STALL_MS after location was set to
  /checkout; a challenge page served at /checkout itself is a normal navigation and can't be seen
  from here). Every case shows the section's error message and re-enables the button.
*/
(function () {
  'use strict';

  var root = document.querySelector('[data-xadv-offer]');
  if (!root) return;

  var CONFIG = {};
  try {
    CONFIG = JSON.parse(root.querySelector('[data-xadv-offer-config]').textContent) || {};
  } catch (e) {
    console.warn('[xall-adv-offer] Invalid config', e);
    return;
  }

  // Shopify's own transport, captured before any vendor script can wrap it later.
  var nativeFetch = window.fetch ? window.fetch.bind(window) : null;
  // cart/add.js alone goes through the page's current fetch: Shopify's shop_events_listener wraps
  // fetch after this script runs and turns a /cart/add request into the standard
  // product_added_to_cart event, which the Meta channel (AddToCart) and other app pixels read.
  // Through nativeFetch the add was invisible to them (verified live 2026-10-29).
  function cartAddFetch() {
    return window.fetch ? window.fetch.bind(window) : nativeFetch;
  }
  var ROUTE_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var STORAGE_KEY = 'xall_adv_attribution';
  var REQUEST_TIMEOUT_MS = 15000;
  var REDIRECT_STALL_MS = 20000;
  var KEYS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id',
    'gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid', 'msclkid',
    'affid', 'sub1', 'sub2', 'sub3', 'sub4', 'sub5'
  ];
  // Cart attributes written by the MPC lander (assets/xall-paid-offer.js: setCartAttributes() and
  // attribution(), incl. its ATTRIBUTION_KEYS) that this page does not write itself. Blanked on
  // handoff so a lander visit earlier in the same browser cannot stamp this order. Keys both
  // files write (UTMs, click ids, affid, sub1-5, offer_id, purchase_mode) are overwritten anyway.
  // Keep in sync if xall-paid-offer.js gains new attribute keys.
  var STALE_MPC_KEYS = [
    'uid', 'landing_page', 'offer_variant', 'supply', 'quantity', 'cadence', 'discount_pct',
    'offer_value', 'currency', 'addons', 'af_source', 'oid'
  ];

  var radios = Array.prototype.slice.call(root.querySelectorAll('[data-xadv-offer-pkg]'));
  var bump = root.querySelector('[data-xadv-offer-bump]');
  var buttons = Array.prototype.slice.call(root.querySelectorAll('[data-xadv-offer-checkout]'));
  var mainButton = root.querySelector('[data-xadv-offer-main]');
  var errorNode = root.querySelector('[data-xadv-offer-error]');
  var sticky = root.querySelector('[data-xadv-offer-sticky]');
  var embedded = root.hasAttribute('data-xadv-offer-embedded');
  var busy = false;
  var redirectTimer = null;

  /* ---------- attribution ---------- */

  function stored() {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}') || {};
    } catch (e) {
      return {};
    }
  }

  function landerPath(saved) {
    var here = window.location.pathname;
    if (embedded) return here;
    if (saved.landing_path && saved.landing_path !== here) return String(saved.landing_path);
    try {
      var ref = new URL(document.referrer);
      if (ref.origin === window.location.origin && ref.pathname !== here) return ref.pathname;
    } catch (e) {}
    return '';
  }

  function attribution() {
    var saved = stored();
    var params = new URLSearchParams(window.location.search);
    var out = {};
    KEYS.forEach(function (key) {
      out[key] = String(params.get(key) || saved[key] || '').slice(0, 500);
    });
    out.lander_path = landerPath(saved);
    out.offer_path = window.location.pathname;
    out.angle = String(CONFIG.angle || params.get('angle') || '');
    // Which article CTA the reader used (assets/xall-adv.js): link, button, sticky, img-<block id>
    // or inline-<n>. Stored only, never read from the URL; blank when absent.
    out.adv_cta = advCta(saved);
    return out;
  }

  // Inside the article a checkout without any earlier CTA click came straight from the box.
  function advCta(saved) {
    return String(saved.adv_cta || (embedded ? 'picker' : '')).slice(0, 100);
  }

  /* ---------- selection ---------- */

  function selectedRadio() {
    for (var i = 0; i < radios.length; i++) if (radios[i].checked) return radios[i];
    return null;
  }

  function money(cents) {
    var format = CONFIG.moneyFormat || '${{amount}}';
    var value = Number(cents || 0) / 100;
    function withDelimiters(number, decimals, thousands, decimal) {
      var parts = number.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    }
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (match, key) {
      switch (key) {
        case 'amount_no_decimals': return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelimiters(value, 2, "'", '.');
        default: return withDelimiters(value, 2, ',', '.');
      }
    });
  }

  function state() {
    var pkg = selectedRadio();
    if (!pkg) return null;
    var price = Number(pkg.getAttribute('data-price')) || 0;
    var bumpOn = !!(bump && bump.checked);
    var bumpPrice = bumpOn ? Number(bump.getAttribute('data-price')) || 0 : 0;
    var subscription = pkg.getAttribute('data-mode') === 'subscription';
    return {
      mode: subscription ? 'subscription' : 'one_time',
      planId: subscription ? Number(pkg.getAttribute('data-plan')) || null : null,
      bonusVariantId: subscription ? Number(pkg.getAttribute('data-bonus-variant')) || null : null,
      bonusQty: subscription ? Number(pkg.getAttribute('data-bonus-qty')) || 0 : 0,
      variantId: Number(pkg.value),
      code: pkg.getAttribute('data-code') || '',
      title: pkg.getAttribute('data-title') || '',
      boxes: Number(pkg.getAttribute('data-boxes')) || 1,
      sku: pkg.getAttribute('data-sku') || '',
      price: price,
      compare: Number(pkg.getAttribute('data-compare')) || 0,
      bump: bumpOn,
      bumpVariantId: bump ? Number(bump.value) : null,
      bumpPrice: bumpPrice,
      bumpSku: bump ? bump.getAttribute('data-sku') || '' : '',
      total: price + bumpPrice
    };
  }

  function render() {
    radios.forEach(function (radio) {
      var card = radio.closest('.xadv-buy-pkg');
      if (card) card.classList.toggle('is-selected', radio.checked);
    });
    var s = state();
    if (!s) return;
    Array.prototype.forEach.call(root.querySelectorAll('[data-xadv-offer-total]'), function (node) {
      node.textContent = money(s.total);
    });
    Array.prototype.forEach.call(root.querySelectorAll('[data-xadv-offer-sum-title]'), function (node) {
      node.textContent = s.title + (s.bump ? ' + 1 box' : '');
    });
    Array.prototype.forEach.call(root.querySelectorAll('[data-xadv-offer-terms]'), function (node) {
      node.hidden = s.mode !== 'subscription';
    });
    // The subscription ships free (terms line says so); the shipping note is for one-time packages.
    Array.prototype.forEach.call(root.querySelectorAll('[data-xadv-offer-ship]'), function (node) {
      node.hidden = s.mode === 'subscription';
    });
  }

  /* ---------- selection memory (back from checkout) ---------- */

  // The inputs are autocomplete="off", so the browser never restores them; a reload or a back
  // navigation from checkout would show the server default package and an unchecked bump.
  // Remember the shopper's choice per offer for this tab and put it back on load and pageshow.
  var CHOICE_KEY = 'xall_adv_offer_choice:' + (CONFIG.offerId || '');

  function saveChoice() {
    var pkg = selectedRadio();
    if (!pkg) return;
    try {
      sessionStorage.setItem(CHOICE_KEY, JSON.stringify({ variantId: pkg.value, bump: !!(bump && bump.checked) }));
    } catch (e) {}
  }

  function restoreChoice() {
    var saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(CHOICE_KEY) || 'null');
    } catch (e) {}
    if (!saved) return;
    for (var i = 0; i < radios.length; i++) {
      if (radios[i].value === String(saved.variantId)) {
        radios[i].checked = true;
        break;
      }
    }
    if (bump) bump.checked = !!saved.bump;
  }

  /* ---------- events ---------- */

  function payload(extra) {
    var s = state();
    var saved = stored();
    var currency = CONFIG.currency || 'USD';
    var base = {
      offer_id: CONFIG.offerId || '',
      angle: String(CONFIG.angle || ''),
      available: !!CONFIG.available,
      currency: currency,
      // Which article CTA brought the reader and the session's first page, from the saved
      // attribution (blank when absent). The arrival event survives where the article's
      // adv_cta_click, sent right before navigation, can be lost.
      adv_cta: advCta(saved),
      landing_path: String(saved.landing_path || '').slice(0, 500)
    };
    if (s) {
      var name = CONFIG.productTitle || '';
      var items = [{
        item_id: String(s.variantId),
        item_name: name,
        item_variant: s.title,
        item_category: 'offer_package',
        price: s.price / 100,
        quantity: 1
      }];
      if (s.bonusVariantId && s.bonusQty) {
        items.push({
          item_id: String(s.bonusVariantId),
          item_name: name,
          item_variant: 'bonus',
          item_category: 'subscription_bonus',
          price: 0,
          quantity: s.bonusQty
        });
      }
      if (s.bump) {
        items.push({
          item_id: String(s.bumpVariantId),
          item_name: name,
          item_variant: 'bump',
          item_category: 'order_bump',
          price: s.bumpPrice / 100,
          quantity: 1
        });
      }
      base.package = s.code;
      base.package_label = s.title;
      base.boxes = s.boxes;
      base.variant_id = s.variantId;
      base.sku = s.sku;
      base.bump = s.bump;
      base.purchase_mode = s.mode;
      base.value = s.total / 100;
      base.ecommerce = { currency: currency, value: s.total / 100, items: items };
      base.content_ids = items.map(function (item) { return item.item_id; });
      base.content_type = 'product';
      base.num_items = items.length;
    }
    return Object.assign(base, extra || {});
  }

  function emit(name, extra) {
    var detail = payload(extra);
    try {
      window.dataLayer = window.dataLayer || [];
      if (detail.ecommerce) window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push(Object.assign({ event: name }, detail));
    } catch (e) {}
    try {
      document.dispatchEvent(new CustomEvent('xall:' + name, { detail: detail }));
    } catch (e) {}
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('xall_' + name, detail);
      }
    } catch (e) {}
  }

  /* ---------- checkout ---------- */

  function setBusy(on) {
    busy = on;
    root.classList.toggle('is-busy', on);
    buttons.forEach(function (button) {
      var label = button.querySelector('[data-xadv-offer-cta-label]') || button;
      if (on) {
        if (!button.hasAttribute('data-label')) button.setAttribute('data-label', label.textContent);
        label.textContent = button.getAttribute('data-busy-label') || label.textContent;
      } else if (button.hasAttribute('data-label')) {
        label.textContent = button.getAttribute('data-label');
      }
      button.disabled = on;
      button.setAttribute('aria-busy', on ? 'true' : 'false');
    });
  }

  function showError(on) {
    if (errorNode) errorNode.hidden = !on;
  }

  // An Error that carries the adv_cart_error fields (step, status, reason).
  function cartError(step, status, reason, message) {
    var error = new Error(message);
    error.step = step;
    error.status = status;
    error.reason = reason;
    return error;
  }

  var CHALLENGE = /verif|challenge|captcha/i;

  function post(path, body) {
    var step = path.replace(/^cart\//, '').replace(/\.js$/, '');
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, REQUEST_TIMEOUT_MS) : null;
    var send = path === 'cart/add.js' ? cartAddFetch() : nativeFetch;
    return send(ROUTE_ROOT + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body || {}),
      signal: controller ? controller.signal : undefined
    }).then(function (response) {
      return response.text().then(function (text) {
        var data = null;
        try {
          data = JSON.parse(text);
        } catch (e) {}
        if (!response.ok) {
          var challenge = response.status === 429 || (data === null && CHALLENGE.test(text));
          throw cartError(step, response.status, challenge ? 'bot_challenge' : 'http_error',
            path + ' ' + response.status + (data && data.description ? ': ' + data.description : ''));
        }
        if (data === null) {
          throw cartError(step, response.status, CHALLENGE.test(text) ? 'bot_challenge' : 'non_json',
            path + ' ' + response.status + ': response is not JSON');
        }
        clearTimeout(timer);
        return data;
      });
    }).catch(function (error) {
      clearTimeout(timer);
      if (error && error.reason) throw error;
      var timedOut = !!(error && error.name === 'AbortError');
      throw cartError(step, 0, timedOut ? 'timeout' : 'network_error',
        path + (timedOut ? ' timed out' : ' failed: ' + String((error && error.message) || error)));
    });
  }

  function cartAttributes(s) {
    var blanks = {};
    STALE_MPC_KEYS.forEach(function (key) { blanks[key] = ''; });
    return Object.assign(blanks, attribution(), {
      offer_id: CONFIG.offerId,
      offer_package: s.code,
      offer_bump: s.bump ? 'yes' : 'no',
      purchase_mode: s.mode,
      offer_plan: s.planId ? String(s.planId) : ''
    });
  }

  function writeAttributes(s) {
    var attributes = cartAttributes(s);
    function attempt() {
      // discount: '' removes every code on the cart (documented Ajax Cart API behavior); this
      // offer is priced by variant and never takes a code. The discount_code cookie is expired
      // separately before /checkout, which would otherwise re-apply it.
      return post('cart/update.js', { attributes: attributes, discount: '' }).then(function (cart) {
        if (!cart || !cart.attributes || cart.attributes.offer_id !== CONFIG.offerId) {
          throw new Error('offer_id not confirmed on cart');
        }
        return cart;
      });
    }
    return attempt().catch(function (first) {
      console.warn('[xall-adv-offer] Cart attributes failed, retrying once', first);
      return attempt().catch(function (second) {
        console.warn('[xall-adv-offer] Cart attributes failed twice; continuing to checkout without them', second);
        emit('adv_attr_fail', { error: String((second && second.message) || second).slice(0, 200) });
      });
    });
  }

  // Every added line must sit on the plan it was sent with: the subscription package on exactly
  // s.planId, every other line on none. Appstle wraps window.fetch and may rewrite selling_plan,
  // so the response is the only proof. Any mismatch removes all added lines and aborts.
  function checkSellingPlans(s) {
    return function (response) {
      var lines = (response && response.items) || [];
      var wrong = lines.filter(function (line) {
        var plan = line && line.selling_plan_allocation && line.selling_plan_allocation.selling_plan;
        var planId = plan ? Number(plan.id) : null;
        var isSub = s.mode === 'subscription' && Number(line.variant_id || line.id) === s.variantId &&
          line.properties && line.properties._offer_package === s.code;
        return isSub ? planId !== s.planId : planId !== null;
      });
      if (s.mode === 'subscription' && !lines.some(function (line) {
        return Number(line.variant_id || line.id) === s.variantId && line.selling_plan_allocation;
      })) {
        wrong = lines;
      }
      if (!wrong.length) return response;
      var updates = {};
      lines.forEach(function (line) { updates[line.key] = 0; });
      return post('cart/update.js', { updates: updates }).catch(function () {}).then(function () {
        throw cartError('add', 200, 'selling_plan', 'A line came back on the wrong selling plan; added lines removed.');
      });
    };
  }

  // Still here long after location was set to /checkout: report it and let the shopper try again.
  // pagehide (leaving, or entering the back/forward cache) cancels the watch.
  function watchRedirect(cta) {
    clearTimeout(redirectTimer);
    redirectTimer = setTimeout(function () {
      redirectTimer = null;
      emit('adv_cart_error', {
        step: 'checkout',
        status: 0,
        reason: 'redirect_stalled',
        cta: cta,
        path: window.location.pathname,
        error: 'Still on the page ' + REDIRECT_STALL_MS / 1000 + ' s after the checkout redirect'
      });
      setBusy(false);
      showError(true);
    }, REDIRECT_STALL_MS);
  }

  function checkout(event) {
    if (busy) return;
    var button = event && event.currentTarget;
    var cta = button && button.hasAttribute && button.hasAttribute('data-xadv-offer-main') ? 'main' : 'sticky';
    var s = state();
    if (!s || !nativeFetch) {
      showError(true);
      return;
    }
    showError(false);
    saveChoice();
    setBusy(true);
    emit('adv_checkout_click');

    var lineProps = { _offer_id: CONFIG.offerId };
    var main = { id: s.variantId, quantity: 1, properties: Object.assign({ _offer_package: s.code }, lineProps) };
    if (s.mode === 'subscription') {
      if (!s.planId) {
        showError(true);
        setBusy(false);
        return;
      }
      main.selling_plan = s.planId;
    }
    var items = [main];
    if (s.bonusVariantId && s.bonusQty) {
      items.push({ id: s.bonusVariantId, quantity: s.bonusQty, properties: Object.assign({ _offer_package: 'bonus' }, lineProps) });
    }
    if (s.bump && s.bumpVariantId) {
      items.push({ id: s.bumpVariantId, quantity: 1, properties: Object.assign({ _offer_package: 'bump' }, lineProps) });
    }

    var start = CONFIG.clearCart ? post('cart/clear.js') : Promise.resolve();
    start
      .then(function () { return post('cart/add.js', { items: items }); })
      .then(checkSellingPlans(s))
      .then(function () { return writeAttributes(s); })
      .then(function () {
        document.cookie = 'discount_code=; Max-Age=0; path=/';
        watchRedirect(cta);
        window.location.href = ROUTE_ROOT + 'checkout';
      })
      .catch(function (error) {
        console.warn('[xall-adv-offer] Checkout handoff failed', error);
        var message = String((error && error.message) || error).slice(0, 200);
        emit('adv_checkout_error', { error: message });
        emit('adv_cart_error', {
          step: (error && error.step) || 'script',
          status: Number(error && error.status) || 0,
          reason: (error && error.reason) || 'script_error',
          cta: cta,
          path: window.location.pathname,
          error: message
        });
        setBusy(false);
        showError(true);
      });
  }

  /* ---------- sticky bar (mobile) ---------- */

  function setupSticky() {
    if (!sticky || !mainButton) return;
    var stickyButton = sticky.querySelector('[data-xadv-offer-checkout]');
    function set(on) {
      sticky.classList.toggle('is-on', on);
      sticky.setAttribute('aria-hidden', on ? 'false' : 'true');
      if (stickyButton) stickyButton.tabIndex = on ? 0 : -1;
    }
    if (!('IntersectionObserver' in window)) {
      set(true);
      return;
    }
    new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        // On only once the main button has scrolled above the viewport or is still below it.
        set(!entry.isIntersecting);
      });
    }).observe(mainButton);
  }

  /* ---------- inside the article: offer view on first sight ---------- */

  function viewWhenSeen() {
    var target = root.querySelector('.xadv-buy__packages') || root;
    if (!('IntersectionObserver' in window)) {
      emit('adv_offer_view');
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var viewport = (entry.rootBounds && entry.rootBounds.height) || window.innerHeight;
          if (entry.intersectionRatio < 0.5 && entry.intersectionRect.height < viewport / 2) return;
          io.disconnect();
          emit('adv_offer_view');
        });
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    io.observe(target);
  }

  /* ---------- wire up ---------- */

  if (CONFIG.available && radios.length) {
    restoreChoice();
    render();
  }
  if (embedded) viewWhenSeen();
  else emit('adv_offer_view');
  if (!CONFIG.available || !radios.length) return;

  radios.forEach(function (radio) {
    radio.addEventListener('change', function () {
      if (!radio.checked) return;
      showError(false);
      saveChoice();
      render();
      emit('adv_package_select');
    });
  });
  if (bump) {
    bump.addEventListener('change', function () {
      saveChoice();
      render();
      emit('adv_bump_toggle');
    });
  }
  buttons.forEach(function (button) {
    button.addEventListener('click', checkout);
  });

  // Back button / bfcache: the page can come back frozen in the loading state. Restore the
  // remembered package and bump either way (bfcache or a normal reload), then re-sync totals.
  window.addEventListener('pagehide', function () {
    clearTimeout(redirectTimer);
    redirectTimer = null;
  });

  window.addEventListener('pageshow', function (event) {
    if (event.persisted || busy) {
      setBusy(false);
      showError(false);
    }
    restoreChoice();
    render();
  });

  setupSticky();
  render();
})();
